<template>
  <div>
    <h6 class="font-weight-bold mb-3">{{ $t("page.groups.members") }}</h6>

    <b-card
      v-for="memberId in members"
      :key="memberId"
      no-body
      class="p-2 mb-2"
    >
      <b-row class="no-gutters align-items-center">
        <b-col class="col-auto mr-3">
          <b-icon icon="person-fill" font-scale="1.2" variant="secondary" />
        </b-col>
        <b-col>
          <template v-if="clientById(memberId)">
            <div class="font-weight-bold">
              {{ clientById(memberId).firstName }} {{ clientById(memberId).lastName }}
            </div>
            <small class="text-muted">{{ clientById(memberId).email }}</small>
          </template>
          <template v-else>
            <small class="text-muted">{{ memberId }}</small>
          </template>
          <b-badge v-if="memberId === adminId" variant="primary" class="ml-2">
            {{ $t("page.groups.adminBadge") }}
          </b-badge>
        </b-col>
        <b-col v-if="memberId !== adminId" class="col-auto">
          <b-button
            size="sm"
            variant="outline-danger"
            @click="confirmRemove(memberId)"
          >
            {{ $t("button.removeMember") }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div v-if="!members || members.length === 0">
      <p class="text-muted text-center">{{ $t("page.groups.noMembers") }}</p>
    </div>

    <hr />

    <h6 class="font-weight-bold mb-2">{{ $t("page.groups.addMember") }}</h6>

    <!-- A user may belong to only one group: the API refuses the add and we
         show the reason here instead of failing silently. -->
    <b-alert :show="!!addMemberError" variant="warning" class="py-2 px-3 mb-2">
      <small>{{ addMemberError }}</small>
    </b-alert>
    <b-alert :show="!!addMemberSuccess" variant="success" class="py-2 px-3 mb-2">
      <small>{{ addMemberSuccess }}</small>
    </b-alert>

    <b-form @submit.prevent="addMember" class="d-flex">
      <b-form-input
        v-model="newMemberEmail"
        type="email"
        :placeholder="$t('page.groups.memberEmailPlaceholder')"
        class="mr-2"
        required
      />
      <b-button type="submit" variant="primary">
        {{ $t("button.addMember") }}
      </b-button>
    </b-form>
  </div>
</template>

<script>
export default {
  props: {
    groupId: {
      type: String,
      required: true,
    },
    adminId: {
      type: String,
      default: null,
    },
    members: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      newMemberEmail: "",
      addMemberError: "",
      addMemberSuccess: "",
    };
  },
  computed: {
    clientList() {
      return this.$store.getters["clients/getClientList"];
    },
  },
  methods: {
    clientById(id) {
      return this.clientList.find(c => c._id === id) || null;
    },
    addMember() {
      if (!this.newMemberEmail) return;
      this.addMemberError = "";
      this.addMemberSuccess = "";
      const email = this.newMemberEmail;
      this.$store.commit("loader/SET_LOADER", {});
      this.$store
        .dispatch("groups/addMemberToGroup", {
          groupId: this.groupId,
          payload: { adminId: this.adminId, email },
        })
        .then(() => {
          this.newMemberEmail = "";
          this.addMemberSuccess = this.$t("page.groups.addMemberSuccess", { email });
        })
        .catch(error => {
          // messageKey = a known refusal (e.g. the user is already in a group),
          // otherwise fall back to whatever the API said.
          const key = error && error.messageKey;
          this.addMemberError = key
            ? this.$t(key)
            : (error && error.apiMessage) || this.$t("page.groups.errAddMemberGeneric");
        })
        .finally(() => this.$store.dispatch("loader/close"));
    },
    confirmRemove(memberId) {
      this.$store.commit("confirm/SET_CONFIRM", {
        titleText: "page.groups.removeMemberTitle",
        messageText: "page.groups.removeMemberMessage",
        actionBtnLabel: "button.confirm",
        actionBtnCallback: () => this.removeMember(memberId),
      });
    },
    removeMember(memberId) {
      this.$store.commit("loader/SET_LOADER", {});
      this.$store
        .dispatch("groups/removeMemberFromGroup", {
          groupId: this.groupId,
          payload: { requesterId: this.adminId, clientId: memberId },
        })
        .catch(() => {})
        .finally(() => this.$store.dispatch("loader/close"));
    },
  },
};
</script>

<style scoped></style>
